import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../../lib/api';
import { useListState } from '../../lib/hooks';
import { money, num, plural, WORDS } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ErrorState, PageHead } from '../../components/ui';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import {
  GRAINS,
  GrainSwitch,
  PeriodNav,
  spanQuery,
  useSyncedGrain,
  type Grain,
  type Span,
} from '../../components/PeriodNav';
import {
  LayoutColumn,
  ProfileLayoutBar,
  ProfileLayoutButton,
  ProfileLayoutProvider,
  Section,
  useProfileLayout,
  type SectionDef,
} from '../../components/ProfileLayout';

interface Stage {
  stage: string;
  label: string;
  short: string;
  order: number;
  color: string;
  probability: number;
  count: number;
  value: number;
  weightedValue: number;
}

interface PipelineData {
  stages: Stage[];
  open: { count: number; value: number; weightedValue: number };
  closed: {
    won: { count: number; value: number };
    lost: { count: number; value: number };
    winRate: number | null;
    averageDealValue: number;
    averageDaysToClose: number | null;
  };
  health: {
    stale: { count: number; value: number };
    overdue: { count: number; value: number };
    noCloseDate: { count: number; value: number };
    staleDays: number;
  };
  forecast: Array<{
    days: number;
    count: number;
    value: number;
    weightedValue: number;
  }>;
  trend: Array<{
    month: string;
    wonCount: number;
    wonValue: number;
    lostCount: number;
    lostValue: number;
  }>;
  /** اسمُ الفترة التي قِيس عليها — من الخادم فهو من يحدّ طرفيها. */
  periodLabel: string;
}

/**
 * ألوانُ القمع تدرّجٌ لا ألوانٌ متفرّقة.
 *
 * كانت كلُّ مرحلةٍ لونًا مستقلًّا: رماديٌّ وأزرقان وأصفران. والأصفران لا
 * يُفرَّق بينهما بعينٍ سليمة (فرقُهما ٨ والحدُّ ١٥)، وتباينُهما على الأبيض
 * ١٫٤ فيغيبان، والرماديُّ يُقرأ «لا بيانات» لا «مرحلةً أولى».
 *
 * والمراحلُ مرتَّبةٌ بطبعها — أوّلُها أوّلٌ وآخرُها آخر — وما كان مرتَّبًا
 * فحقُّه تدرّجٌ في لونٍ واحد: يفتَح في أوّل الطريق ويعتِم كلّما قرُب
 * الإغلاق. فالعينُ تقرأ التقدّم بلا أن تحفظ مفتاحَ ألوان.
 *
 * واللونُ يتبع رتبةَ المرحلة لا موضعَها في المعروض: من أخفى مرحلةً لم
 * تتبدّل ألوانُ الباقيات تحته.
 */
const STAGE_RAMP: Array<{ bg: string; ink: string }> = [
  { bg: '#C3D4F9', ink: '#16202F' },
  { bg: '#93B2F3', ink: '#16202F' },
  { bg: '#5E8EEE', ink: '#16202F' },
  { bg: '#3672EA', ink: '#FFFFFF' },
  { bg: '#1D46A6', ink: '#FFFFFF' },
];

const toneOf = (order: number) =>
  STAGE_RAMP[Math.min(Math.max(order, 1), STAGE_RAMP.length) - 1];




interface PeriodData {
  bucket: Grain;
  offset: number;
  /** اسمُ الفترة كما يصوغه الخادم: «سبتمبر ٢٠٢٦»، «أسبوع ٠٥/٠٩». */
  label: string;
  from: string;
  to: string;
  target: number;
  won: { count: number; value: number };
  lost: { count: number; value: number };
  winRate: number | null;
  achieved: number | null;
  quotations: {
    issued: number;
    accepted: number;
    pending: number;
    refused: number;
    acceptRate: number | null;
  };
}

/**
 * أقسامُ القمع — يرتّبها قارئُها.
 *
 * من يقرأ هذه الشاشة ثلاثةٌ: مديرٌ يريد النتيجةَ والهدفَ أوّلًا، ومسؤولُ
 * قمعٍ يريد المراحلَ وصحّتَها، ومحلّلٌ يريد الخدماتِ والمصادر. وترتيبٌ
 * واحدٌ يخدم واحدًا ويُتعب اثنين — فصار الترتيبُ تفضيلًا: يُسحب القسمُ
 * إلى موضعه، ويُخفى ما لا يُقرأ، ويُكبَّر واحدُه على الشاشة كاملةً.
 *
 * والعريضُ في العمود الرئيس والضيّقُ في الجانبيّ ابتداءً — ولمن شاء غيرُه.
 */
const PIPELINE_SECTIONS: SectionDef[] = [
  /* ما لا يُقسَم: قمعٌ برسمه، وهدفٌ بشريطه، وخطُّ شهورٍ باثني عشر عمودًا. */
  { key: 'stages', title: 'المراحل', column: 'full' },
  { key: 'cohort', title: 'قمع التصفية', column: 'full' },
  { key: 'results', title: 'النتائج والهدف', column: 'full' },
  { key: 'trend', title: 'المحسوم على المدى', column: 'full' },
  { key: 'conversion', title: 'معدّلات التحويل', column: 'full' },
  { key: 'weighted', title: 'القيمة المرجّحة لكل مرحلة', column: 'full' },
  /*
   * وما يُقسَم يُقرن بقرينه.
   *
   * العمودان يُملآن كلٌّ على حدة، فترتيبُهما هو ما يصنع الصفوف: أوّلُ
   * الرئيس بجوار أوّل الجانبيّ. فيُرتَّبان ليقع كلُّ تقريرٍ إلى جانب ما
   * يُقرأ معه — الصحّةُ مع التوقّع فكلاهما عن المفتوح، والخدماتُ مع
   * المصادر فكلاهما جدولُ جودة.
   */
  { key: 'health', title: 'صحّة القمع', column: 'main' },
  { key: 'services', title: 'أكثر الخدمات طلبًا', column: 'main' },
  { key: 'forecast', title: 'المتوقَّع إغلاقه', column: 'side' },
  { key: 'sources', title: 'جودة المصادر', column: 'side' },
];

const HIDDEN_STAGES_KEY = 'goyone.funnel.hiddenStages';

/**
 * المراحل المخفيّة من القمع — اختيارٌ يخصّ قارئه فيُحفظ في متصفّحه.
 *
 * ويُخزَّن المخفيُّ لا المعروض عمدًا: الفراغ يعني «أظهِر الكلّ» فلا يحتاج
 * الحفظُ معرفةَ قائمة المراحل مسبقًا، ومرحلةٌ تُضاف إلى النظام لاحقًا تظهر
 * تلقائيًّا بدل أن تختفي لأنها ليست في قائمةٍ قديمة.
 *
 * ولا يُحفظ في الخادم: هو تفضيلُ عرضٍ لا سياسةُ شركة، وفرضُه على الفريق كلّه
 * يجعل من أخفى مرحلةً لنفسه يخفيها عن غيره.
 */
function useHiddenStages(): [Set<string>, (next: Set<string>) => void] {
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_STAGES_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const save = (next: Set<string>) => {
    setHidden(next);
    try {
      localStorage.setItem(HIDDEN_STAGES_KEY, JSON.stringify([...next]));
    } catch {
      /* الاختيار يعمل هذه الجلسة ولو تعذّر الحفظ. */
    }
  };

  return [hidden, save];
}

export function Pipeline() {
  const { state, patch } = useListState({ bucket: 'month', offset: '0' });
  const master: Span = {
    grain: (state.bucket as Grain) ?? 'month',
    offset: Number(state.offset) || 0,
    from: state.from,
    to: state.to,
  };
  /* كتبةٌ واحدة: الحبيبةُ والإزاحةُ ومحوُ المدى معًا — الرابطُ لا يُكتب مرّتين. */
  const setMaster = (g: Grain | null, o: number) =>
    patch({
      bucket: g ?? 'month',
      offset: String(o),
      from: undefined,
      to: undefined,
    });
  const setRange = (from?: string, to?: string) => patch({ from, to });

  const [hiddenKeys, setHiddenKeys] = useHiddenStages();
  const [picking, setPicking] = useState(false);
  const layout = useProfileLayout('goyone.layout.pipeline', PIPELINE_SECTIONS);

  const pipeline = useQuery({
    queryKey: ['pipeline', master],
    queryFn: () =>
      api<PipelineData>(`/sales/deals/pipeline${qs(spanQuery(master))}`),
    placeholderData: (prev) => prev,
  });

  if (pipeline.isError) {
    return (
      <div className="page">
        <ErrorState error={pipeline.error} onRetry={() => void pipeline.refetch()} />
      </div>
    );
  }

  /*
   * القمعُ أصلُه بلا زمن، ويقبل فترةً بمعنًى واحدٍ صادق: الصفقاتُ التي
   * دخلته في تلك الفترة، أين تقف اليوم. و«الكل» هو الأصل.
   */
  const [stageGrain, setStageGrain] = useState<Grain | null>(null);
  const [stageOffset, setStageOffset] = useState(0);
  const [stageRange, setStageRange] = useState<{ from?: string; to?: string }>({});
  /* القمعُ وحدَه يبقى على «الكل» ما لم يُطلب غيرُه: أصلُه بلا زمن. */
  useEffect(() => {
    setStageOffset(master.offset);
    setStageGrain((g) => (g === null ? null : master.grain));
    setStageRange({});
  }, [master.grain, master.offset, master.from, master.to]);
  /* القمعُ يتبع المدى المكتوب إن وُجد، وإلّا فحبيبتَه — و«الكل» أصلُه. */
  const stageSpan =
    stageRange.from && stageRange.to
      ? { from: stageRange.from, to: stageRange.to }
      : master.from && master.to
        ? { from: master.from, to: master.to }
        : stageGrain
          ? { bucket: stageGrain, offset: stageOffset }
          : {};
  const stageQuery = useQuery({
    queryKey: ['pipeline', 'stages', stageSpan],
    queryFn: () =>
      api<{ label: string | null; stages: Stage[] }>(
        `/sales/deals/stages${qs(stageSpan)}`,
      ),
    placeholderData: (prev) => prev,
  });

  const data = pipeline.data;
  const allStages = stageQuery.data?.stages ?? data?.stages ?? [];
  const stages = allStages.filter((s) => !hiddenKeys.has(s.stage));
  const hiddenCount = allStages.length - stages.length;
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <div className="page">
      <PageHead
        title="قمع المبيعات"
        subtitle="أين تقف الصفقات، وكم منها ينجح"
        actions={
          <>
            {/*
              فلترُ الصفحة — قائدُ ما فيها.

              كانت قائمةَ مددٍ متدحرجة («آخر ٩٠ يومًا») تتحرّك كلَّ يوم فلا
              يُقارَن قياسٌ بقياس. صارت فترةً لها أوّلٌ وآخر بالشكل نفسه
              الذي في البطاقات، وتغييرُها يجرّ الصفحةَ كلَّها إليها.
            */}
            <PeriodNav
              grain={master.grain}
              onGrain={(g) => setMaster(g, 0)}
              offset={master.offset}
              onOffset={(o) => setMaster(master.grain, o)}
              label={pipeline.data?.periodLabel ?? ''}
              range={{ from: master.from, to: master.to }}
              onRange={setRange}
            />
            <ProfileLayoutButton api={layout} />
            <Link to="/sales/lost" className="btn">
              <Icon name="alert" size={15} />
              تحليل الخسائر
            </Link>
          </>
        }
      />

      {/* بطاقات النظام: يختار المستخدم ما يُعرض منها، وتدخل بحركةٍ كبقيّته. */}
      {data && (
        <RecordStats
          items={pipelineCards(data)}
          storageKey="goyone.stats.pipeline"
          defaults={['open', 'weighted', 'winRate', 'wonValue', 'avgValue']}
        />
      )}

      {/*
        القمع مرسوم بعرض متناسب مع عدد الصفقات.
        الشكل يُظهر أين يضيق المسار، وهي المعلومة التي لا يعطيها جدول أرقام.
      */}
      <ProfileLayoutProvider api={layout}>
      <ProfileLayoutBar api={layout} />
      {/* عمودٌ بعرض الشاشة أوّلًا، ثمّ العمودان تحته. */}
      <LayoutColumn api={layout} column="full">

      <Section layoutKey="stages">
      <div className="card mb-4">
        <div className="card-head">
          <h2 className="card-title">المراحل</h2>
          <PeriodNav
            grain={stageGrain}
            onGrain={(g) => {
              setStageGrain(g);
              setStageOffset(0);
              setStageRange({});
            }}
            offset={stageOffset}
            onOffset={setStageOffset}
            label={stageQuery.data?.label ?? ''}
            withAll
            range={
              stageRange.from ? stageRange : { from: master.from, to: master.to }
            }
            onRange={(from, to) =>
              setStageRange(from && to ? { from, to } : {})
            }
          />
          <span className="row" style={{ gap: 'var(--sp-2)' }}>
            <span className="small mute">
              {stageQuery.data?.label
                ? 'المفتوحة التي بدأت في هذه الفترة'
                : 'الصفقات المفتوحة فقط'}
            </span>
            <button
              className={`btn btn-sm btn-icon btn-ghost${
                hiddenCount > 0 ? ' btn-primary' : ''
              }`}
              title="اختر مراحل القمع"
              aria-label="اختر مراحل القمع"
              aria-expanded={picking}
              onClick={() => setPicking((v) => !v)}
            >
              <Icon name="layers" size={15} />
            </button>
          </span>
        </div>

        {/*
          القمع يبنيه صاحبه من مراحل الصفقات القائمة.
          ولا تُخترع مرحلةٌ جديدة هنا: المرحلة تحكم احتمالية الصفقة وتقاريرها
          كلها، فمرحلةٌ في العرض وحده تُنشئ قمعًا لا يطابق ما خلفه. والمخفيّة
          تبقى في الصفقات ولا تُحذف.
        */}
        {picking && (
          <div className="card-body funnel-picker">
            <div className="tiny mute mb-2">المراحل المعروضة في قمعك.</div>
            {allStages.map((s) => (
              <label key={s.stage} className="row funnel-picker-row">
                <input
                  type="checkbox"
                  checked={!hiddenKeys.has(s.stage)}
                  onChange={(e) => {
                    const next = new Set(hiddenKeys);
                    if (e.target.checked) next.delete(s.stage);
                    else next.add(s.stage);
                    /* لا يُترك القمع فارغًا: قمعٌ بلا مرحلةٍ لا يقول شيئًا. */
                    if (next.size < allStages.length) setHiddenKeys(next);
                  }}
                />
                <span
                  className="dot"
                  style={{ background: toneOf(s.order).bg }}
                  aria-hidden
                />
                <span className="small">{s.label}</span>
                <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
                  {num(s.count)}
                </span>
              </label>
            ))}
            {hiddenCount > 0 && (
              <button
                className="btn btn-sm mt-2"
                style={{ width: '100%' }}
                onClick={() => setHiddenKeys(new Set())}
              >
                إظهار كل المراحل
              </button>
            )}
          </div>
        )}

        <div className="card-body">
          {pipeline.isPending ? (
            <div className="col">
              {Array.from({ length: 5 }, (_, i) => (
                <div className="skeleton" key={i} style={{ height: 46 }} />
              ))}
            </div>
          ) : data?.open.count === 0 ? (
            <p className="mute small center">
              لا توجد صفقات مفتوحة. تُنشأ الصفقة عند تحويل عميل محتمل أو من شاشة الصفقات.
            </p>
          ) : (
            <>
            <div className="fn">
              {stages.map((s, i) => {
                /*
                 * عرضُ الشريحة بعدد صفقاتها، وله حدٌّ أدنى.
                 *
                 * شريحةٌ فيها صفقةٌ واحدةٌ أمام ستّ عشرة تصير خيطًا لا
                 * يسع اسمَها ولا رقمَها، فتُقرأ «صفر» وهي ليست صفرًا.
                 * فالنسبةُ تُرسم فوق أرضيّةٍ ثابتة: الشكلُ يقول أين يضيق
                 * المسار، والرقمُ المكتوبُ فيه يقول كم بالضبط.
                 */
                const w = (c: number) => 30 + (c / maxCount) * 70;
                const top = w(s.count);
                const bottom = stages[i + 1] ? w(stages[i + 1].count) : top;
                const tone = toneOf(s.order);
                /* نسبةُ عددها إلى ما قبلها — أين يضيق المسار وأين يتّسع. */
                const prev = i > 0 ? stages[i - 1].count : null;
                const ratio =
                  prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;

                return (
                  <div className="fn-stage" key={s.stage}>
                    {ratio !== null && (
                      <div
                        className="fn-link"
                        title={`عددُ هذه المرحلة ${ratio}٪ من عدد التي قبلها — صورةُ اللحظة لا تتبّعُ صفقةٍ بعينها`}
                      >
                        <span className={`fn-link-pct${ratio < 100 ? ' is-down' : ' is-up'}`}>
                          تحويل <b className="num">{ratio}٪</b>
                        </span>
                      </div>
                    )}
                    <Link
                      to={`/sales/deals?stage=${s.stage}&openOnly=true`}
                      className="fn-band"
                      title={`${s.label} — ${plural(s.count, WORDS.deal)} بقيمة ${money(s.value)}`}
                    >
                      <span
                        className="fn-shape"
                        style={{
                          background: tone.bg,
                          clipPath: `polygon(${50 - top / 2}% 0%, ${50 + top / 2}% 0%, ${50 + bottom / 2}% 100%, ${50 - bottom / 2}% 100%)`,
                        }}
                        aria-hidden
                      />
                      <span className="fn-text" style={{ color: tone.ink }}>
                        <span className="fn-name">{s.label}</span>
                        <span className="fn-figs">
                          <b className="num">{num(s.count)}</b>
                          <span className="num">{money(s.value)}</span>
                        </span>
                      </span>
                    </Link>
                  </div>
                );
              })}
            </div>

            {/*
              حدٌّ صادق: هذه صورةُ اللحظة لا رحلةُ صفقة.
              القمعُ يعدّ ما هو مفتوحٌ الآن في كلّ مرحلة، ولا يتتبّع صفقةً
              من مرحلةٍ إلى التي تليها — فالنسبةُ بين شريحتين شكلُ القمع
              اليوم لا «معدّلُ تحوّل» يُبنى عليه قرار.
            */}
            {/*
              ونسبةُ الطريق كلِّه: من أوّل مرحلةٍ إلى آخرها.
              النِّسبُ بين الشرائح تقول أين يضيق المسار خطوةً خطوة، وهذه
              تقول ما بقي منه في آخره — وهما رقمان مختلفان لا يُغني أحدهما.
            */}
            {stages.length > 1 && stages[0].count > 0 && (
              <div className="fn-total">
                <span className="fn-total-label">
                  من {stages[0].short} إلى {stages[stages.length - 1].short}
                </span>
                <b className="num fn-total-pct">
                  {Math.round(
                    (stages[stages.length - 1].count / stages[0].count) * 100,
                  )}
                  ٪
                </b>
              </div>
            )}

            <p className="tiny mute mt-3 center">
              صورةُ اللحظة للمفتوح: كم صفقةً تقف الآن في كلّ مرحلة. والنسبةُ بين
              شريحتين مقارنةُ عددٍ بعدد، لا تتبّعُ صفقةٍ في رحلتها.
            </p>
            </>
          )}
        </div>
      </div>

      </Section>

      <Section layoutKey="cohort">
        <CohortFunnel master={master} />
      </Section>

      <Section layoutKey="results">
        <PeriodResults master={master} />
      </Section>

      <Section layoutKey="trend">
        <ClosedTrend master={master} />
      </Section>

      </LayoutColumn>

      <div className="two-col pl-two">
      <LayoutColumn api={layout} column="main">

      <Section layoutKey="conversion">
        <ConversionCard master={master} />
      </Section>

      <Section layoutKey="services">
        <TopServices master={master} />
      </Section>

      </LayoutColumn>

      <LayoutColumn api={layout} column="side">

      <Section layoutKey="health">
        {data && <HealthCard data={data} />}
      </Section>

      <Section layoutKey="forecast">
        {data && <ForecastCard data={data} />}
      </Section>

      <Section layoutKey="weighted">
        <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">القيمة المرجّحة لكل مرحلة</h2>
          </div>
          <div className="card-body">
            {/*
              مربّعاتٌ جنبًا إلى جنب لا قائمةٌ رأسيّة.

              القائمةُ تُقرأ سطرًا سطرًا فتُقارَن بالحساب؛ والمربّعاتُ
              متجاورةٌ تُقارَن بالعين. ولكلٍّ شريطُ حصّته من مجموع المرجَّح،
              ولونُ مرحلته من التدرّج نفسه — فيُعرف أين يثقل المتوقَّع.
            */}
            <div className="wv">
              {(() => {
                const top = Math.max(
                  1,
                  ...stages.map((x) => x.weightedValue),
                );
                const sum = stages.reduce((n, x) => n + x.weightedValue, 0) || 1;
                return stages.map((st) => {
                  const tone = toneOf(st.order);
                  return (
                    <Link
                      key={st.stage}
                      to={`/sales/deals?stage=${st.stage}&openOnly=true`}
                      className="wv-cell"
                      style={{ ['--wv-tone' as string]: tone.bg }}
                      title={`${st.label} — مرجَّحٌ ${money(st.weightedValue)} من ${money(st.value)} باحتمالية ${st.probability}٪`}
                    >
                      <span className="wv-head">
                        <span className="wv-dot" aria-hidden />
                        {st.short}
                      </span>
                      <span className="num wv-value">
                        {money(st.weightedValue)}
                      </span>
                      <span className="wv-bar">
                        <span
                          className="wv-bar-fill"
                          style={{
                            height: `${Math.max(4, (st.weightedValue / top) * 100)}%`,
                          }}
                        />
                      </span>
                      <span className="tiny mute">
                        {Math.round((st.weightedValue / sum) * 100)}٪ من المرجَّح
                      </span>
                    </Link>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </Section>

      <Section layoutKey="sources">
        <SourceQuality master={master} />
      </Section>

      </LayoutColumn>
      </div>
      </ProfileLayoutProvider>
    </div>
  );
}

interface CohortData {
  label: string;
  entered: number;
  /** كم صفقةً تاريخُها مسجَّلٌ فعلًا لا مستنتَجٌ من مرحلتها. */
  tracked: number;
  stages: Array<{
    stage: string;
    label: string;
    short: string;
    order: number;
    count: number;
    value: number;
  }>;
  won: { count: number; value: number };
  lost: { count: number; value: number };
}

/**
 * قمعُ التصفية — من دخل في فترةٍ، كم منهم بلغ كلَّ مرحلة.
 *
 * «المراحل» فوقه صورةُ لحظة: أين تقف الصفقاتُ المفتوحة اليوم، وهي لا تنزل
 * بالضرورة — قد يتكوّم العملُ في التفاوض فيزيد على العرض، وذاك خبرٌ نافع.
 * وهذا يجيب السؤال الآخر: «مئةٌ تواصلنا معهم، كم منهم بلغ العرض؟» — فينزل
 * أبدًا ولا يتّسع، لأنّ من بلغ التفاوضَ قد بلغ العرضَ قبله بالضرورة.
 *
 * وهما سؤالان لا يُغني أحدُهما عن الآخر، فيُعرضان معًا.
 */
function CohortFunnel({ master }: { master: Span }) {
  const c = useSyncedGrain(master);
  const q = useQuery({
    queryKey: ['pipeline', 'cohort', c.span],
    queryFn: () =>
      api<CohortData>(`/sales/deals/cohort${qs(spanQuery(c.span))}`),
    placeholderData: (prev) => prev,
  });

  const d = q.data;
  const now = d?.label ?? GRAINS.find((g) => g.key === c.grain)!.now;
  const rows = d?.stages ?? [];
  const top = rows[0]?.count ?? 0;

  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">قمع التصفية</h2>
        <PeriodNav
          grain={c.grain}
          onGrain={(g) => g && c.setGrain(g)}
          offset={c.offset}
          onOffset={c.setOffset}
          label={now}
          range={c.range}
          onRange={c.setRange}
        />
        <span className="small mute">
          الصفقات التي دخلت في هذه الفترة — إلى أين بلغت
        </span>
      </div>
      <div className="card-body">
        {!d ? (
          <div className="skeleton" style={{ height: 320 }} />
        ) : top === 0 ? (
          <p className="tiny mute center">لا صفقات دخلت في هذه الفترة.</p>
        ) : (
          <>
            <div className="fn">
              {rows.map((st, i) => {
                /* العرضُ بحصّة المرحلة من أوّلها، وله حدٌّ أدنى يسع نصَّها. */
                const w = (n: number) => 30 + (n / top) * 70;
                const next = rows[i + 1];
                const tone = toneOf(st.order);
                const prev = i > 0 ? rows[i - 1].count : null;
                const step =
                  prev && prev > 0 ? Math.round((st.count / prev) * 100) : null;
                return (
                  <div className="fn-stage" key={st.stage}>
                    {step !== null && (
                      <div
                        className="fn-link"
                        title={`${st.count} من ${prev} بلغوا ${st.label}`}
                      >
                        <span className="fn-link-pct is-down">
                          تحويل <b className="num">{step}٪</b>
                        </span>
                      </div>
                    )}
                    <div className="fn-band is-static">
                      <span
                        className="fn-shape"
                        style={{
                          background: tone.bg,
                          clipPath: `polygon(${50 - w(st.count) / 2}% 0%, ${50 + w(st.count) / 2}% 0%, ${50 + w(next ? next.count : st.count) / 2}% 100%, ${50 - w(next ? next.count : st.count) / 2}% 100%)`,
                        }}
                        aria-hidden
                      />
                      <span className="fn-text" style={{ color: tone.ink }}>
                        <span className="fn-name">{st.label}</span>
                        <span className="fn-figs">
                          <b className="num">{num(st.count)}</b>
                          <span className="num">{money(st.value)}</span>
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* المصير: ما حُسم من الدفعة نفسها. */}
            <div className="ch-end">
              <span className="ch-end-item is-won">
                <span className="tiny">فازت</span>
                <b className="num">{num(d.won.count)}</b>
                <span className="num tiny">{money(d.won.value)}</span>
              </span>
              <span className="ch-end-item is-lost">
                <span className="tiny">خسرت</span>
                <b className="num">{num(d.lost.count)}</b>
                <span className="num tiny">{money(d.lost.value)}</span>
              </span>
              <span className="ch-end-item">
                <span className="tiny">ما زالت مفتوحة</span>
                <b className="num">
                  {num(d.entered - d.won.count - d.lost.count)}
                </b>
                <span className="num tiny">من {num(d.entered)}</span>
              </span>
            </div>

            {/*
              حدٌّ صادق: ما قبل تفعيل التتبّع لا تاريخَ له.
              فمرحلةُ الصفقة الحاليّة تُقرأ أبعدَ ما بلغت — وهو صادقٌ في
              الأغلب لأنّ المراحل تُقطع بترتيبها، لكنّه استنتاجٌ لا تسجيل.
              فيُقال كم منها مسجَّلٌ فعلًا، ولا يُترك القارئ يظنّ الكلَّ متتبَّعًا.
            */}
            <p className="tiny mute mt-3 center">
              {d.tracked === d.entered
                ? `تاريخُ الانتقال مسجَّلٌ لـ${plural(d.entered, WORDS.deal)} كاملةً.`
                : `تاريخُ الانتقال مسجَّلٌ لـ${num(d.tracked)} من ${plural(d.entered, WORDS.deal)}؛ والباقي تُقرأ مرحلتُه الحاليّة أبعدَ ما بلغ — تسجيلُ الانتقالات بدأ حديثًا ويكتمل مع العمل.`}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** يجلب نتائجَ الفترة الجارية بحبيبتها — يُستعمل في بطاقتين. */
function usePeriod(span: Span) {
  return useQuery({
    queryKey: ['pipeline', 'period', span],
    queryFn: () =>
      api<PeriodData>(`/sales/deals/period${qs(spanQuery(span))}`),
    placeholderData: (prev) => prev,
  });
}

/**
 * نتائجُ الفترة الجارية — وإلى أين بلغت من هدفها.
 *
 * «آخر تسعين يومًا» مدّةٌ متدحرجة لا يُحاسَب عليها أحد: الهدفُ يُوضع
 * لأسبوعٍ أو شهرٍ أو سنةٍ بعينها، فالمقارنةُ به تحتاج فترةً لها أوّلٌ
 * وآخر. فصارت الفترةُ تُختار، ومعها هدفُها من الإعدادات.
 *
 * ولا هدفَ صفرٌ: من لم يضع هدفًا لا يُقال له إنّه قصّر عنه، ويُدَلّ على
 * مكان وضعه بدل رقمٍ لا معنى له.
 */
function PeriodResults({ master }: { master: Span }) {
  const c = useSyncedGrain(master);
  const p = usePeriod(c.span);
  const d = p.data;
  /* اسمُ الفترة من الخادم: هو من يعرف أين تبدأ ومتى تنتهي. */
  const now = d?.label ?? GRAINS.find((g) => g.key === c.grain)!.now;
  const decided = (d?.won.count ?? 0) + (d?.lost.count ?? 0);

  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">النتائج والهدف</h2>
        <PeriodNav
          grain={c.grain}
          onGrain={(g) => g && c.setGrain(g)}
          offset={c.offset}
          onOffset={c.setOffset}
          label={now}
          range={c.range}
          onRange={c.setRange}
        />
      </div>
      <div className="card-body">
        {!d ? (
          <div className="skeleton" style={{ height: 120 }} />
        ) : (
          <>
            {/*
              الهدفُ أوّلًا: الرقمُ المطلوب، وما تحقّق منه، وشريطٌ بينهما.
              وإن تجاوز المئةَ فُصل الفائضُ بلونٍ ثانٍ — بلوغُ الهدف حدثٌ
              يُرى لا رقمٌ يُقرأ.
            */}
            {d.target > 0 ? (
              <div className="tg">
                <span className="tg-head">
                  <span className="tg-label">هدف {now}</span>
                  <b className="num tg-target">{money(d.target)}</b>
                </span>
                {/*
                  التدرّجُ يقول القربَ من الهدف بلا رقم.

                  لونٌ واحدٌ للشريط كلِّه يقفز عند حدٍّ مصطنع: تسعةٌ
                  وخمسون تحذيرٌ وستّون علامة، والفرقُ بينهما واحد. فيُمدّ
                  التدرّجُ على المسار كلِّه من الأحمر في أوّله إلى الأخضر في
                  آخره، ويُكشَف منه بقدر ما تحقّق — فطرفُ الشريط هو لونُ
                  حالك، ويخضرّ كلّما قاربت.
                */}
                <span className="bar-track tg-track">
                  <span
                    className="tg-fill"
                    style={{
                      width: `${Math.min(100, d.achieved ?? 0)}%`,
                      ['--tg-pct' as string]: Math.max(1, Math.min(100, d.achieved ?? 0)),
                    }}
                  >
                    <span className="tg-grad" />
                  </span>
                </span>
                <span className="tg-foot">
                  <span className="num tg-done">{money(d.won.value)}</span>
                  <span className="tiny mute">محقَّقٌ من الفائزة</span>
                  <b className={`num tg-pct${(d.achieved ?? 0) >= 100 ? ' is-hit' : ''}`}>
                    {d.achieved ?? 0}٪
                  </b>
                </span>
              </div>
            ) : (
              <p className="tiny mute mb-3">
                لا هدف مضبوطٌ لهذه الفترة — يُضبط في{' '}
                <Link to="/settings/commissions" className="link">
                  إعدادات المبيعات ← الأهداف البيعية
                </Link>
                .
              </p>
            )}

            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>النتيجة</th>
                    <th className="col-num">العدد</th>
                    <th className="col-num">القيمة</th>
                    <th className="col-num">النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'ناجحة', row: d.won, color: 'var(--success)' },
                    { label: 'خاسرة', row: d.lost, color: 'var(--danger)' },
                  ].map((r) => (
                    <tr key={r.label}>
                      <td>
                        <span className="row">
                          <span
                            className="dot"
                            style={{ background: r.color, width: 8, height: 8 }}
                            aria-hidden
                          />
                          {r.label}
                        </span>
                      </td>
                      <td className="num">{num(r.row.count)}</td>
                      <td className="num strong">{money(r.row.value)}</td>
                      <td className="num cell-mute">
                        {decided > 0
                          ? `${Math.round((r.row.count / decided) * 100)}٪`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * معدّلات التحويل — نِسبتان تُقاسان، ومصيرُ ما صدر في شريطٍ واحد.
 *
 * كانت خمسةَ مربّعاتٍ بيضاء: رقمان نسبتان وثلاثةٌ أعداد. والنِّسبةُ رقمٌ
 * بلا مقياس لا يُقرأ — «٨٧٪» لا يُعرف أقريبٌ هو من التمام أم بعيد إلا
 * بمسطرة. والأعدادُ الثلاثة ليست ثلاثةَ أشياء: العروضُ الصادرة هي مجموعُ
 * المقبولة والمرفوضة وما لم يُحسم بعد. فعرضُها متجاورةً يخفي أنّها كلٌّ
 * وأجزاؤه، ويترك السؤالَ الوحيد الذي يُسأل — كم بقي معلّقًا؟ — بلا جواب.
 *
 * فصارت: مقياسان للنسبتين، وشريطٌ واحدٌ يقسم ما صدر على مصائره الثلاثة
 * بدليلٍ يحمل الأعداد — فاللونُ لا يحمل هويّةً وحده.
 */
function ConversionCard({ master }: { master: Span }) {
  const c = useSyncedGrain(master);
  const p = usePeriod(c.span);
  const d = p.data;
  const now = d?.label ?? GRAINS.find((g) => g.key === c.grain)!.now;
  const q = d?.quotations;
  const closed = q?.refused ?? 0;
  const pending = q?.pending ?? 0;
  const total = q?.issued || 1;

  const parts = [
    {
      key: 'ok',
      label: 'مقبولة',
      count: q?.accepted ?? 0,
      tone: 'var(--success)',
    },
    { key: 'wait', label: 'تنتظر ردًّا', count: pending, tone: 'var(--brand)' },
    { key: 'no', label: 'مرفوضة أو منتهية', count: closed, tone: 'var(--danger)' },
  ];

  const meters = [
    {
      key: 'quote',
      label: 'قبول العروض',
      pct: q?.acceptRate ?? null,
      note: `من ${plural((q?.accepted ?? 0) + closed, WORDS.quotation)} محسومة`,
    },
    {
      key: 'deal',
      label: 'فوز الصفقات',
      pct: d?.winRate ?? null,
      note: `${num(d?.won.count ?? 0)} فائزة · ${num(d?.lost.count ?? 0)} خاسرة`,
    },
  ];

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">معدّلات التحويل</h2>
        <PeriodNav
          grain={c.grain}
          onGrain={(g) => g && c.setGrain(g)}
          offset={c.offset}
          onOffset={c.setOffset}
          label={now}
          range={c.range}
          onRange={c.setRange}
        />
      </div>
      <div className="card-body">
        <div className="cv-meters">
          {meters.map((m) => (
            <div className="cv-meter" key={m.key}>
              <span className="cv-meter-head">
                <span className="cv-meter-label">{m.label}</span>
                <b className="num cv-meter-pct">
                  {m.pct == null ? '—' : `${m.pct}٪`}
                </b>
              </span>
              {/*
                المقياسُ يُلوَّن بما يقيس: ما فوق النصف أخضر، وما دونه
                يتدرّج إلى الأحمر. ولا لونَ لما لا قياسَ له — «لا بيانات»
                غيرُ «صفر»، وصبغُه أحمرَ يُقرأ فشلًا وهو غيابُ قياس.
              */}
              <span className="bar-track">
                <span
                  className="bar-fill"
                  style={{
                    width: `${m.pct ?? 0}%`,
                    background:
                      m.pct == null
                        ? 'var(--ink-4)'
                        : m.pct >= 50
                          ? 'var(--success)'
                          : m.pct >= 30
                            ? 'var(--warning)'
                            : 'var(--danger)',
                  }}
                />
              </span>
              <span className="tiny mute">{m.note}</span>
            </div>
          ))}
        </div>

        <div className="cv-split">
          <span className="cv-split-head">
            <span className="cv-split-label">مصير ما صدر</span>
            <b className="num">{num(q?.issued ?? 0)}</b>
            <span className="tiny mute">عرضًا</span>
          </span>
          <span className="cv-bar">
            {parts
              .filter((p) => p.count > 0)
              .map((p) => (
                <span
                  key={p.key}
                  className="cv-seg"
                  style={{
                    width: `${(p.count / total) * 100}%`,
                    background: p.tone,
                  }}
                  title={`${p.label} — ${num(p.count)}`}
                />
              ))}
          </span>
          <span className="cv-key">
            {parts.map((p) => (
              <span key={p.key} className="cv-key-item">
                <span className="dot" style={{ background: p.tone }} aria-hidden />
                {p.label}
                <b className="num">{num(p.count)}</b>
              </span>
            ))}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * صحّةُ المفتوح والمتوقَّعُ إغلاقُه.
 *
 * القمعُ يقول كم فيه، ولا يقول أصالحٌ ما فيه. وقمعٌ ممتلئٌ قد يكون ممتلئًا
 * بالموتى: صفقاتٌ لم يلمسها أحدٌ منذ شهر، وأخرى فات موعدُ إغلاقها المسجَّل
 * وما زالت مفتوحة، وثالثةٌ بلا موعدٍ أصلًا فلا تدخل توقّعًا ولا تُذكِّر
 * بنفسها. وقيمتُها كلُّها تُحتسب في «المتوقّع تحصيله» فتضخّمه.
 *
 * وإلى جانبها ما يُنتظر إغلاقُه بمواعيد الفريق المسجَّلة — لا ترجيحًا
 * للقمع كلِّه بلا زمن، فذاك يخلط ما يُغلق هذا الشهر بما يُغلق بعد سنة.
 */
/**
 * صحّةُ المفتوح — ثلاثُ دوائرَ تُقرأ كلٌّ وحدها.
 *
 * القمعُ يقول كم فيه ولا يقول أصالحٌ ما فيه: قمعٌ ممتلئٌ قد يكون ممتلئًا
 * بالموتى — صفقاتٌ لم يلمسها أحدٌ منذ شهر، وأخرى فات موعدُ إغلاقها وهي
 * مفتوحة، وثالثةٌ بلا موعدٍ أصلًا. وقيمتُها كلُّها تُحتسب في «المتوقّع
 * تحصيله» فتضخّمه.
 */
function HealthCard({ data }: { data: PipelineData }) {
  const h = data.health;
  const openValue = data.open.value || 1;
  const risks = [
    {
      key: 'stale',
      label: `راكدة منذ ${plural(h.staleDays, WORDS.day)}`,
      hint: 'لم يُسجَّل عليها نشاطٌ ولا تعديل',
      row: h.stale,
      tone: 'var(--warning)',
    },
    {
      key: 'overdue',
      label: 'فات موعد إغلاقها',
      hint: 'الموعد المسجَّل مضى وهي مفتوحة',
      row: h.overdue,
      tone: 'var(--danger)',
    },
    {
      key: 'nodate',
      label: 'بلا موعد إغلاق',
      hint: 'لا تدخل التوقّع ولا تُذكِّر بنفسها',
      row: h.noCloseDate,
      tone: 'var(--ink-4)',
    },
  ];
  return (
    <div className="card mb-4 is-twin">
        <div className="card-head">
          <h2 className="card-title">صحّة القمع</h2>
          <span className="tiny mute">من الصفقات المفتوحة</span>
        </div>
        <div className="card-body">
          {/*
            ثلاثُ دوائر لا ثلاثةُ أشرطة.

            الشريطُ يُقرأ طولًا فيُقارَن بأخيه؛ وهذه ثلاثةُ أعطالٍ مختلفة
            لا يُقارَن بعضُها ببعض — تُقرأ كلُّ واحدةٍ وحدها: كم من مال
            القمع معطَّلٌ بهذا السبب. والقوسُ يقول النسبةَ بلمحة، والعددُ
            في وسطه، وما تحته يفسّره. والدائرةُ تكبر عند اللمس فتُعرف
            أنّها تُنقر — والنقرُ يفتح صفقاتها المفتوحة.
          */}
          <div className="hz">
            {risks.map((r) => {
              /* القوسُ نسبةُ قيمتها إلى قيمة المفتوح كلِّه — كم من المال معطَّل. */
              const share = Math.min(
                100,
                Math.round((r.row.value / openValue) * 100),
              );
              return (
                <Link
                  key={r.key}
                  to="/sales/deals?result=OPEN"
                  className="hz-cell"
                  title={`${r.label} — ${plural(r.row.count, WORDS.deal)} بقيمة ${money(r.row.value)} (${share}٪ من قيمة المفتوح)`}
                >
                  <span
                    className="hz-ring"
                    style={{
                      ['--hz-pct' as string]: `${share}%`,
                      ['--hz-tone' as string]: r.tone,
                    }}
                  >
                    <span className="hz-ring-in">
                      <b className="num hz-count">{num(r.row.count)}</b>
                      <span className="num hz-share">{share}٪</span>
                    </span>
                  </span>
                  <span className="hz-label">{r.label}</span>
                  <span className="num hz-value">{money(r.row.value)}</span>
                  <span className="tiny mute hz-hint">{r.hint}</span>
                </Link>
              );
            })}
          </div>
          <p className="tiny mute center mt-3">
            القوسُ حصّةُ كلٍّ من قيمة المفتوح، والعددُ في وسطه. وقد تُحسب
            الصفقةُ في أكثر من دائرة — راكدةٌ وفات موعدُها معًا.
          </p>
        </div>
      </div>
  );
}

/**
 * المتوقَّعُ إغلاقُه بمواعيد الفريق المسجَّلة — لا ترجيحًا للقمع كلِّه بلا
 * زمن، فذاك يخلط ما يُغلق هذا الشهر بما يُغلق بعد سنة.
 */
function ForecastCard({ data }: { data: PipelineData }) {
  return (
    <div className="card mb-4 is-twin">
        <div className="card-head">
          <h2 className="card-title">المتوقَّع إغلاقه</h2>
          <span className="tiny mute">بمواعيد الإغلاق المسجَّلة</span>
        </div>
        <div className="card-body">
{/*
            بطاقةٌ لكلّ مدًى بأيقونتها وقوسها.

            مربّعان بأرقامٍ عارية لا يقولان أيّهما أقرب ولا كم يزيد الأبعدُ
            على الأقرب. فصار لكلٍّ حلقةٌ تحمل حصّتَه من الأبعد: الثلاثون
            جزءٌ من التسعين، فتُرى النسبةُ بينهما بلا حساب.
          */}
          <div className="fc-grid">
            {data.forecast.map((f, i) => {
              const top = Math.max(
                1,
                ...data.forecast.map((x) => x.weightedValue),
              );
              const share = Math.round((f.weightedValue / top) * 100);
              return (
                <div
                  key={f.days}
                  className={`fc-cell${i === 0 ? ' is-near' : ''}`}
                  title={`${plural(f.count, WORDS.deal)} موعدُها خلال ${plural(f.days, WORDS.day)} — قيمتُها ${money(f.value)}`}
                >
                  <span className="fc-icon">
                    <Icon name={i === 0 ? 'zap' : 'calendar'} size={16} />
                  </span>
                  <span className="fc-when">
                    خلال {plural(f.days, WORDS.day)}
                  </span>
                  <span className="fc-value num">{money(f.weightedValue)}</span>
                  <span className="fc-arc">
                    <span
                      className="fc-arc-fill"
                      style={{ width: `${share}%` }}
                    />
                  </span>
                  <span className="tiny mute">
                    مرجَّحًا من {money(f.value)} · {plural(f.count, WORDS.deal)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="tiny mute mt-3">
            ما فات موعدُه لا يُحسب هنا — هو في «صحّة القمع» ينتظر تصحيح موعده.
            وصفقةٌ بلا موعدٍ لا تظهر في التوقّع أصلًا.
          </p>
        </div>
      </div>
  );
}

/**
 * خطُّ الشهور — أصاعدٌ نحن أم هابطون؟
 *
 * رقمٌ واحدٌ للمدّة كلّها يخفي شهرًا انهار وآخرَ عوّضه. فيُقسَّم المحسومُ
 * على شهوره: الرابحُ فوق الخطّ والخاسرُ تحته. والاتجاهُ يُقرأ بالموضع لا
 * باللون وحده — فمن لا يميّز الأخضر من الأحمر يقرأ فوقًا وتحتًا.
 */
interface TrendRow {
  key: string;
  label: string;
  title: string;
  wonCount: number;
  wonValue: number;
  lostCount: number;
  lostValue: number;
}

/**
 * أيُّ نافذةٍ تضمّ فترةَ الرئيس؟
 *
 * إزاحةُ البطاقات فترةٌ واحدة (شهرٌ إلى الوراء)، وإزاحةُ هذا الخطّ نافذةٌ
 * كاملة (اثنا عشر شهرًا). فنقلُ الرقم كما هو يقفز بالخطّ سنةً حين ينتقل
 * الرئيسُ شهرًا — يتبعه ظاهرًا ويخالفه معنًى. فيُترجَم: أيُّ نافذةٍ تقع
 * فيها فترةُ الرئيس؟ فيُري الخطُّ سياقَ ما يُقرأ في البطاقات.
 */
function windowHolding(grain: Grain, offset: number): number {
  if (grain === 'month') {
    const now = new Date();
    const at = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return Math.max(0, now.getFullYear() - at.getFullYear());
  }
  return Math.floor(offset / 12);
}

function ClosedTrend({ master }: { master: Span }) {
  const [grain, setGrain] = useState<Grain>(master.grain);
  const [offset, setOffset] = useState(() =>
    windowHolding(master.grain, master.offset),
  );
  useEffect(() => {
    setGrain(master.grain);
    setOffset(windowHolding(master.grain, master.offset));
  }, [master.grain, master.offset]);
  const q = useQuery({
    queryKey: ['pipeline', 'trend', grain, offset],
    queryFn: () =>
      api<{ bucket: Grain; label: string; rows: TrendRow[] }>(
        `/sales/deals/trend${qs({ bucket: grain, offset })}`,
      ),
    placeholderData: (prev) => prev,
  });

  const rows = q.data?.rows ?? [];
  /*
   * اسمُ النافذة من الخادم: هو من يعرف أنّها سنةٌ تقويميّةٌ لا اثنا عشر
   * شهرًا متدحرجة — و«سنة ٢٠٢٦» أوضحُ من «يناير — ديسمبر».
   */
  const span = q.data?.label ?? '—';
  const max = Math.max(
    1,
    ...rows.map((r) => Math.max(r.wonValue, r.lostValue)),
  );
  const totalWon = rows.reduce((n, r) => n + r.wonValue, 0);
  const totalLost = rows.reduce((n, r) => n + r.lostValue, 0);

  return (
    <div className="card mb-4">
      <div className="card-head">
        <h2 className="card-title">المحسوم على المدى</h2>
        <PeriodNav
          grain={grain}
          onGrain={(g) => {
            if (!g) return;
            setGrain(g);
            /* نافذةٌ جديدة تبدأ من أحدثها — لا من موضع النافذة القديمة. */
            setOffset(0);
          }}
          offset={offset}
          onOffset={setOffset}
          label={span}
        />
      </div>
      <div className="card-body">
        {q.isPending ? (
          <div className="skeleton" style={{ height: 190 }} />
        ) : rows.length === 0 ? (
          <p className="tiny mute center">لا صفقات محسومة في هذا المدى.</p>
        ) : (
          <div className="tr-chart">
            {rows.map((r) => (
              <div className="tr-col" key={r.key}>
                <span className="tr-up">
                  <span
                    className="tr-bar is-won"
                    style={{ height: `${(r.wonValue / max) * 100}%` }}
                    title={`${r.title} — رابحة: ${plural(r.wonCount, WORDS.deal)} بقيمة ${money(r.wonValue)}`}
                  />
                </span>
                <span className="tr-axis" />
                <span className="tr-down">
                  <span
                    className="tr-bar is-lost"
                    style={{ height: `${(r.lostValue / max) * 100}%` }}
                    title={`${r.title} — خاسرة: ${plural(r.lostCount, WORDS.deal)} بقيمة ${money(r.lostValue)}`}
                  />
                </span>
                <span className="tr-month num" title={r.title}>
                  {r.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/*
          الدليلُ تحت الرسم لا في رأسه.

          الرأسُ يحمل العنوانَ والتنقّلَ والحبيبة، وزيادةُ مجموعين عليه
          تعصر العنوانَ حتى ينكسر ثلاثةَ أسطر. والدليلُ موضعُه عند ما
          يدلّ عليه: تحت الأعمدة التي يفسّرها.
        */}
        {rows.length > 0 && (
          <div className="tr-key">
            <span className="tr-key-item">
              <span
                className="dot"
                style={{ background: 'var(--success)', width: 8, height: 8 }}
                aria-hidden
              />
              رابحة <b className="num">{money(totalWon)}</b>
            </span>
            <span className="tr-key-item">
              <span
                className="dot"
                style={{ background: 'var(--danger)', width: 8, height: 8 }}
                aria-hidden
              />
              خاسرة <b className="num">{money(totalLost)}</b>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * بطاقات القمع.
 *
 * «المتوقّع تحصيله» أصدق من قيمة المفتوح الخام: الأخيرة تفترض نجاح كل صفقة،
 * وهو ما لا يقع. و«لا بيانات» تُقال حين لا يُحسم شيء — صفرٌ في معدّل النجاح
 * يُقرأ فشلًا وهو غيابُ قياس.
 */
function pipelineCards(d: PipelineData): RecordStat[] {
  const c = d.closed;
  return [
    {
      key: 'open',
      label: 'صفقات مفتوحة',
      group: 'المفتوح',
      icon: 'funnel',
      color: '#3672EA',
      value: d.open.count,
      numeric: true,
      note: money(d.open.value),
    },
    {
      key: 'weighted',
      label: 'المتوقّع تحصيله',
      group: 'المفتوح',
      icon: 'chartBar',
      color: '#51A521',
      value: money(d.open.weightedValue),
      numeric: true,
      note: 'مرجَّحٌ باحتمالية كل مرحلة',
    },
    {
      key: 'winRate',
      label: 'معدّل النجاح',
      group: 'الأداء',
      icon: 'zap',
      color:
        c.winRate == null
          ? '#9BA3AE'
          : c.winRate >= 50
            ? '#51A521'
            : c.winRate >= 30
              ? '#BF930B'
              : '#DC2626',
      value: c.winRate == null ? 'لا بيانات' : `${c.winRate}٪`,
      numeric: true,
      note: `${num(c.won.count)} ناجحة مقابل ${num(c.lost.count)} خاسرة`,
    },
    {
      key: 'wonValue',
      label: 'قيمة الصفقات الناجحة',
      group: 'الأداء',
      icon: 'wallet',
      color: '#1D46A6',
      value: money(c.won.value),
      numeric: true,
    },
    {
      key: 'lostValue',
      label: 'قيمة الخاسرة',
      group: 'الأداء',
      icon: 'alert',
      color: c.lost.count ? '#DC2626' : '#9BA3AE',
      value: money(c.lost.value),
      numeric: true,
      note: `${num(c.lost.count)} صفقة`,
    },
    {
      key: 'avgValue',
      label: 'متوسط قيمة الصفقة',
      group: 'الأداء',
      icon: 'receipt',
      color: '#8791A1',
      value: money(c.averageDealValue),
      numeric: true,
    },
    {
      key: 'stale',
      label: 'صفقات راكدة',
      group: 'المفتوح',
      icon: 'clock',
      color: d.health.stale.count ? '#BF930B' : '#9BA3AE',
      value: d.health.stale.count,
      numeric: true,
      note: `${money(d.health.stale.value)} — بلا حركةٍ منذ ${plural(d.health.staleDays, WORDS.day)}`,
    },
    {
      key: 'overdue',
      label: 'فات موعد إغلاقها',
      group: 'المفتوح',
      icon: 'alert',
      color: d.health.overdue.count ? '#DC2626' : '#9BA3AE',
      value: d.health.overdue.count,
      numeric: true,
      note: money(d.health.overdue.value),
    },
    {
      key: 'due30',
      label: 'يُتوقَّع إغلاقه خلال شهر',
      group: 'المفتوح',
      icon: 'calendar',
      color: '#6D5BD0',
      value: money(d.forecast[0]?.weightedValue ?? 0),
      numeric: true,
      note: `مرجَّحًا · ${plural(d.forecast[0]?.count ?? 0, WORDS.deal)}`,
    },
    {
      key: 'avgDays',
      label: 'متوسط مدة الإغلاق',
      group: 'الأداء',
      icon: 'clock',
      color: '#5596FF',
      value:
        c.averageDaysToClose == null ? 'لا بيانات' : plural(c.averageDaysToClose, WORDS.day),
      numeric: true,
      note: 'على الناجحة وحدها',
    },
  ];
}

interface QualityData {
  periodLabel: string;
  conversion: {
    quotationsIssued: number;
    quotationsAccepted: number;
    quotationsRejected: number;
    quotationsExpired: number;
    quotationAcceptRate: number | null;
    dealsWon: number;
    dealsLost: number;
    dealWinRate: number | null;
  };
  bySource: Array<{
    source: string;
    deals: number;
    won: number;
    lost: number;
    open: number;
    wonValue: number;
    value: number;
    winRate: number | null;
  }>;
  topServices: Array<{
    name: string;
    asked: number;
    askedValue: number;
    sold: number;
    soldValue: number;
    acceptRate: number | null;
  }>;
}

/**
 * جودة البيع — ماذا يُطلب، ومن أين يأتي، وكم يتحوّل.
 *
 * ثلاثة تقارير تقيس الجودة لا الحجم: الحجم يقوله القمع أعلاه، وهذه تقول
 * أين يُربح وأين يُهدر الجهد.
 */
/**
 * تقريرا الجودة — كلٌّ قسمٌ مستقلّ.
 *
 * كانا في مكوّنٍ واحدٍ يرسم صفًّا من بطاقتين، فلم يُمكن سحبُ إحداهما دون
 * الأخرى ولا إخفاؤها وحدها. ولمّا صارت الصفحةُ تُرتَّب بيد قارئها صار
 * لكلٍّ مكوّنُها — وطلبُهما واحدٌ يتقاسمانه، فلا استعلامَ مكرَّر.
 */
function useQuality(master: Span) {
  return useQuery({
    queryKey: ['pipeline', 'quality', master],
    queryFn: () =>
      api<QualityData>(`/sales/deals/quality${qs(spanQuery(master))}`),
    placeholderData: (prev) => prev,
  });
}

function TopServices({ master }: { master: Span }) {
  const q = useQuality(master);
  if (q.isError)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <div className="skeleton mb-4" style={{ height: 240 }} />;
  const maxAsked = Math.max(1, ...q.data.topServices.map((s) => s.asked));
  return (
    <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">أكثر الخدمات طلبًا</h2>
            <span className="tiny mute duo-key">
              <span className="duo-key-swatch" aria-hidden />
              طُلبت · بيعت
            </span>
          </div>
          <div className="card-body">
            {q.data.topServices.length === 0 ? (
              <p className="mute center tiny">لا بنود في هذه المدة.</p>
            ) : (
              <div className="table-wrap qt-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الخدمة</th>
                      <th className="col-num">طُلبت</th>
                      <th className="col-num">بيعت</th>
                      <th className="col-num">القبول</th>
                      <th className="col-num">قيمة المباع</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.topServices.map((s) => (
                      <tr key={s.name}>
                        <td>
                          <div className="cell-strong">{s.name}</div>
                          {/*
                            شريطان في مسارٍ واحد: الفاتحُ ما طُلب والغامقُ
                            ما بيع منه. فيُرى الترتيبُ ونسبةُ التحوّل معًا
                            قبل أن يُقرأ رقم — وخدمةٌ تُطلب كثيرًا ولا تُباع
                            تُرى فجوتُها بالعين.
                          */}
                          <div className="bar-track is-duo mt-2">
                            <span
                              className="bar-fill"
                              style={{ width: `${(s.asked / maxAsked) * 100}%` }}
                              title={`طُلبت ${num(s.asked)}`}
                            />
                            <span
                              className="bar-fill is-strong"
                              style={{ width: `${(s.sold / maxAsked) * 100}%` }}
                              title={`بيعت ${num(s.sold)}`}
                            />
                          </div>
                        </td>
                        <td className="num">{num(s.asked)}</td>
                        <td className="num cell-mute">{num(s.sold)}</td>
                        <td className="num cell-mute">
                          {s.acceptRate == null ? '—' : `${s.acceptRate}٪`}
                        </td>
                        <td className="num cell-strong">{money(s.soldValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
  );
}

function SourceQuality({ master }: { master: Span }) {
  const q = useQuality(master);
  if (q.isError)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <div className="skeleton mb-4" style={{ height: 240 }} />;
  const maxDeals = Math.max(1, ...q.data.bySource.map((s) => s.deals));
  return (
    <div className="card mb-4">
          <div className="card-head">
            <h2 className="card-title">جودة المصادر</h2>
            <span className="tiny mute duo-key">
              <span className="duo-key-swatch" aria-hidden />
              صفقات · فوز
            </span>
          </div>
          <div className="card-body">
            {q.data.bySource.length === 0 ? (
              <p className="mute center tiny">لا صفقات في هذه المدة.</p>
            ) : (
              <>
                <div className="table-wrap qt-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>المصدر</th>
                        <th className="col-num">صفقات</th>
                        <th className="col-num">فوز</th>
                        <th className="col-num">قيمة الفوز</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.data.bySource.map((s) => (
                        <tr key={s.source}>
                          <td>
                            <div className="cell-strong">{s.source}</div>
                            {/* الفاتحُ كلُّ صفقاته والغامقُ ما فاز منها. */}
                            <div className="bar-track is-duo mt-2">
                              <span
                                className="bar-fill"
                                style={{ width: `${(s.deals / maxDeals) * 100}%` }}
                                title={`${plural(s.deals, WORDS.deal)}`}
                              />
                              <span
                                className="bar-fill is-strong"
                                style={{ width: `${(s.won / maxDeals) * 100}%` }}
                                title={`فاز منها ${num(s.won)}`}
                              />
                            </div>
                          </td>
                          <td className="num">{num(s.deals)}</td>
                          <td className="num cell-mute">
                            {s.winRate == null ? '—' : `${s.winRate}٪`}
                          </td>
                          <td className="num cell-strong">{money(s.wonValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/*
                  حدٌّ صادق: النظام لا يقرأ من منصّات الإعلانات.
                  عرضُ هذا كأنه «أداء الحملات» يوهم بربطٍ غير موجود، فيُبنى
                  عليه قرارُ إنفاق.
                */}
                <p className="tiny mute mt-4">
                  المصدر هو ما يُدخله الفريق في الصفقة، لا مسحوبٌ من منصّات الإعلانات — فلا
                  يُقاس به إنفاقٌ ولا عائد. ولقياس حملةٍ بعينها، وحِّد كتابة مصدرها عند التسجيل.
                </p>
              </>
            )}
          </div>
        </div>
  );
}
